import { getWebSocketUrl, logger } from './utils';
import apiEndpoints from './api/endpoints';

// Constants for WebSocket service
const WS_RECONNECT_INTERVAL = 3000; // 3 seconds
const WS_MAX_RECONNECT_ATTEMPTS = 3; // Reduced to avoid too many attempts
const WS_STATE_REQUEST_THROTTLE = 5000; // 5 seconds between state requests
const WS_STATE_MAX_REQUESTS = 2; // Maximum number of state requests before giving up
const GLOBAL_REQUEST_LIMIT = 3; // Global limit for concurrent requests - increased to handle account switching and shared stories

/**
 * WebSocket service class to manage WebSocket connections
 */
class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.stateRequestCount = 0;
    this.stateRequestTimer = null;
    this.stateRequestInProgress = false;
    this.currentStoryId = null;
    this.globalRequestCount = 0;
    this.reconnectPending = false;
    this._intentionalDisconnect = false;
    this._connectionInProgress = false;
    this._currentRequestId = null;
    this.handlers = {
      message: new Set(),
      error: new Set(),
      open: new Set(),
      close: new Set()
    };
    this.isConnecting = false;
    
    // Track active rooms (stories, chats, etc.)
    this._activeRooms = new Set();
    
    // Track which components are using which rooms
    // Format: { roomId: Set(["component1", "component2"]) }
    this._roomUsage = {};
    
    // Store authentication parameters
    this._authParams = null;
    
    // Debug info
    this._lastErrorTime = null;
    this._errorCount = 0;
    
    // Create a special error logger that rate limits log messages
    this._logError = this._createRateLimitedLogger('error', 3000);
    this._logWarning = this._createRateLimitedLogger('warn', 3000);
  }
  
  /**
   * Creates a rate-limited logger function
   * @param {string} level - Log level (log, warn, error)
   * @param {number} minInterval - Minimum time between logs in ms
   * @returns {Function} Rate-limited logger function
   * @private
   */
  _createRateLimitedLogger(level = 'log', minInterval = 1000) {
    let lastLog = 0;
    let suppressedCount = 0;
    
    return (message, ...args) => {
      const now = Date.now();
      if (now - lastLog < minInterval) {
        suppressedCount++;
        return;
      }
      
      // If we suppressed messages, add that to the log
      const logMessage = suppressedCount > 0 
        ? `${message} (${suppressedCount} similar messages suppressed)`
        : message;
      
      console[level](logMessage, ...args);
      lastLog = now;
      suppressedCount = 0;
    };
  }

  /**
   * Check if we're already at global request limit
   * @private
   */
  _isAtRequestLimit() {
    return this.globalRequestCount >= GLOBAL_REQUEST_LIMIT;
  }
  
  /**
   * Increment global request counter
   * @private
   */
  _incrementRequestCount() {
    this.globalRequestCount++;
  }
  
  /**
   * Decrement global request counter
   * @private
   */
  _decrementRequestCount() {
    this.globalRequestCount = Math.max(0, this.globalRequestCount - 1);
  }
  
  /**
   * Reset all retry counters and timers
   * @private
   */
  _resetRetryState() {
    this.reconnectAttempts = 0;
    this.stateRequestCount = 0;
    this.stateRequestInProgress = false;
    this.reconnectPending = false;
    this.globalRequestCount = 0;
    
    // Clear timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.stateRequestTimer) {
      clearTimeout(this.stateRequestTimer);
      this.stateRequestTimer = null;
    }
  }

  /**
   * Connect to WebSocket server with optional authentication parameters
   * @param {Object} authParams - Optional authentication parameters from AuthContext
   * @returns {Promise} Promise that resolves when connection is established
   */
  connect(authParams) {
    // Use provided auth params or fall back to stored params
    const effectiveAuthParams = authParams || this._authParams;
    
    // Store the auth params for future use
    if (authParams) {
      this._authParams = authParams;
    }
    
    // If we're intentionally disconnecting, don't try to connect
    if (this._intentionalDisconnect) {
      this._logWarning('WebSocket connect called during intentional disconnect, ignoring');
      return Promise.reject(new Error('Disconnecting in progress'));
    }
    
    // If we already have a socket, close it properly first to ensure clean reconnection
    if (this.socket) {
      if (this.socket.readyState === WebSocket.OPEN || 
          this.socket.readyState === WebSocket.CONNECTING) {
        logger.debug('WebSocket', 'Closing existing connection before creating new one');
        // Temporarily disable intentional disconnect flag to avoid suppressing reconnect
        const wasIntentionalDisconnect = this._intentionalDisconnect;
        this._intentionalDisconnect = false;
        
        // Prevent automatic reconnection handling for this specific close
        const originalOnClose = this.socket.onclose;
        this.socket.onclose = (event) => {
          logger.debug('WebSocket', 'Socket closed during reconnect');
        };
        
        try {
          this.socket.close();
        } catch (e) {
          logger.debug('WebSocket', 'Error closing socket:', e);
        }
        
        this.socket = null;
        this._intentionalDisconnect = wasIntentionalDisconnect;
      }
    }

    // If we're already trying to connect, don't start another connection
    if (this.isConnecting || this._connectionInProgress) {
      this._logWarning('WebSocket connection already in progress');
      return Promise.resolve(this.socket);
    }
    
    // If we're at the request limit, don't try to connect
    if (this._isAtRequestLimit()) {
      this._logWarning('At global request limit, delaying WebSocket connection');
      return Promise.reject(new Error('Too many concurrent requests'));
    }
    
    // Set both flags to ensure we don't get race conditions
    this._incrementRequestCount();
    this.isConnecting = true;
    this._connectionInProgress = true;
    
    // Get the WebSocket URL with authentication parameters if provided
    const websocketBaseURL = getWebSocketUrl(effectiveAuthParams);
    
    // Log detailed auth information for debugging
    logger.debug('WebSocket', `Connecting with auth parameters:`, {
      hasParams: !!effectiveAuthParams,
      userId: effectiveAuthParams?.userId,
      profileId: effectiveAuthParams?.profileId
    });
    
    return new Promise((resolve, reject) => {
      // Set a master timeout for the entire connection process
      const masterTimeout = setTimeout(() => {
        if (this.isConnecting || this._connectionInProgress) {
          this._logWarning('Master connection timeout reached, cleaning up');
          this.isConnecting = false;
          this._connectionInProgress = false;
          this._decrementRequestCount();
          
          // Don't touch the socket, just abandon the process
        }
      }, 10000); // 10 second master timeout
      
      const cleanup = () => {
        clearTimeout(masterTimeout);
        this._connectionInProgress = false;
        this._decrementRequestCount();
      };
      
      try {
        this.socket = new WebSocket(websocketBaseURL);
        
        const connectionTimeout = setTimeout(() => {
          // If we haven't connected after 5 seconds, force an error
          if (this.socket && this.socket.readyState !== WebSocket.OPEN) {
            this._logWarning('WebSocket connection timeout');
            try {
              this.socket.close();
            } catch (err) {
              // Ignore any errors from closing
            }
          }
        }, 5000);
        
        this.socket.onopen = (event) => {
          // Connection established
          clearTimeout(connectionTimeout);
          
          // Reset all retry counters (but carefully)
          this.reconnectAttempts = 0;
          this.stateRequestCount = 0;
          this.reconnectPending = false;
          
          // Clear timers
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          
          this.isConnecting = false;
          this.handlers.open.forEach(handler => handler(event));
          
          // If we reconnected and have a story ID, rejoin the story
          if (this.currentStoryId) {
            console.log(`Rejoining story ${this.currentStoryId} after reconnection`);
            this.sendMessage({
              type: 'join_story',
              story_id: this.currentStoryId
            }).catch(error => {
              this._logWarning(`Failed to rejoin story ${this.currentStoryId} after reconnection:`, error);
            });
          }
          
          resolve(this.socket);
          cleanup();
        };
        
        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Enhanced logging to diagnose update frequency issues
            if (process.env.NODE_ENV === 'development') {
              // Track WebSocket update types by type
              if (!window._wsUpdates) {
                window._wsUpdates = { story_update: 0, page_update: 0, title_update: 0, other: 0 };
              }
              
              if (data.type === 'story_update') {
                window._wsUpdates.story_update++;
                console.log(`[WebSocket] Received story_update #${window._wsUpdates.story_update}`, {
                  fields: Object.keys(data.data || {}),
                  hasPages: !!(data.data && data.data.pages),
                  pageCount: (data.data && data.data.pages) ? data.data.pages.length : 0
                });
              } else if (data.type === 'page_update') {
                window._wsUpdates.page_update++;
              } else if (data.type === 'title_update') {
                window._wsUpdates.title_update++;
              } else {
                window._wsUpdates.other++;
              }
              
              logger.debug('WebSocket', `Message received: ${data.type}`);
            }
            
            // If this is a story update, dispatch a custom event for ReadAloudContext
            if (data.type === 'story_update' && data.data && data.data.id) {
              // Dispatch event for ReadAloudContext to listen for story updates
              document.dispatchEvent(new CustomEvent('storyUpdate', {
                detail: {
                  storyId: data.data.id,
                  story: data.data,
                  isInitialState: data.is_initial_state || false
                }
              }));
            }
            
            // Also dispatch custom event for page updates since ReadAloudContext needs to respond to these too
            if (data.type === 'page_update' && data.data) {
              // For page updates, we now get the complete story state
              // This simplifies the code as we don't need to handle different data formats
              document.dispatchEvent(new CustomEvent('pageUpdate', {
                detail: {
                  storyId: data.data.id,
                  story: data.data,
                  updatedPageId: data.updated_page_id
                }
              }));
              
              // Also dispatch a storyUpdate event for consistency
              document.dispatchEvent(new CustomEvent('storyUpdate', {
                detail: {
                  storyId: data.data.id,
                  story: data.data
                }
              }));
            }
            
            // Note: With our standardized format, we likely won't need separate title_update
            // messages, but keep this for backward compatibility
            if (data.type === 'title_update' && data.data && data.data.id && data.data.title) {
              // Dispatch a specific title update event
              document.dispatchEvent(new CustomEvent('titleUpdate', {
                detail: {
                  storyId: data.data.id,
                  title: data.data.title
                }
              }));
            }
            
            // Handle story_left message from server
            if (data.type === 'story_left' && data.story_id) {
              logger.debug('WebSocket', `Received story_left for story ${data.story_id}`);
              // Remove from active rooms
              this._activeRooms.delete(data.story_id);
              // Clear currentStoryId if it matches
              if (this.currentStoryId === data.story_id) {
                this.currentStoryId = null;
              }
              
              // Dispatch a story_left event
              document.dispatchEvent(new CustomEvent('storyLeft', {
                detail: {
                  storyId: data.story_id
                }
              }));
            }
            
            // Process all messages through registered handlers
            this.handlers.message.forEach(handler => {
              try {
                handler(data);
              } catch (error) {
                console.error('Error in WebSocket message handler:', error);
              }
            });
          } catch (error) {
            this._logError('Error parsing WebSocket message:', error);
            console.error('Raw message data:', event.data);
            this.handlers.error.forEach(handler => handler(error));
          }
        };
        
        this.socket.onerror = (error) => {
          clearTimeout(connectionTimeout);
          
          // Track errors for debugging
          const now = Date.now();
          if (this._lastErrorTime && now - this._lastErrorTime < 1000) {
            this._errorCount++;
          } else {
            this._errorCount = 1;
          }
          this._lastErrorTime = now;
          
          // Log error with count to detect rapid errors
          this._logError(`WebSocket error (${this._errorCount}):`, error);
          
          this.handlers.error.forEach(handler => handler(error));
          this.isConnecting = false;
          
          // Only try to fall back if we haven't had too many rapid errors
          if (this._errorCount <= 3) {
            // Try to use the REST API as a fallback if we have a story ID
            if (this.currentStoryId && this.stateRequestCount < WS_STATE_MAX_REQUESTS && 
                !this._isAtRequestLimit() && !this.stateRequestInProgress && !this._intentionalDisconnect) {
              console.log('Falling back to REST API after WebSocket error');
              this._fetchStateWithThrottle(this.currentStoryId).catch(() => {
                // Error is already handled in _fetchStateWithThrottle
              });
            }
          } else {
            this._logWarning('Too many rapid errors, stopping retry attempts');
          }
          
          reject(error);
          cleanup();
        };
        
        this.socket.onclose = (event) => {
          clearTimeout(connectionTimeout);
          
          if (this._intentionalDisconnect) {
            console.log('WebSocket intentionally closed:', event.code, event.reason);
          } else {
            console.log('WebSocket connection closed:', event.code, event.reason);
          }
          
          this.handlers.close.forEach(handler => handler(event));
          
          // Don't attempt to reconnect if:
          // 1. It was intentionally closed
          // 2. We already have a reconnect pending
          // 3. We've had too many errors
          // 4. We're intentionally disconnecting
          if (event.code !== 1000 && !this.reconnectPending && 
              this._errorCount <= 3 && !this._intentionalDisconnect) {
            this._scheduleReconnect();
          } else {
            this.isConnecting = false;
          }
          
          cleanup();
        };
      } catch (error) {
        this._logError('Error creating WebSocket:', error);
        this.isConnecting = false;
        reject(error);
        cleanup();
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    // Avoid reconnecting too quickly after a disconnect
    const now = Date.now();
    if (this._lastDisconnectTime && now - this._lastDisconnectTime < 1000) {
      console.log('Multiple disconnect calls in short succession, ignoring');
      return;
    }
    this._lastDisconnectTime = now;
    
    // First, mark that we're intentionally disconnecting
    this._intentionalDisconnect = true;
    
    // Clear any pending state
    this._resetRetryState();
    
    // Close the socket if it exists
    if (this.socket) {
      try {
        this.socket.close(1000, 'Closed by client');
      } catch (error) {
        console.warn('Error closing WebSocket:', error);
      }
      
      // Don't set to null yet, let the close event handle it
    }
    
    // Reset error tracking
    this._errorCount = 0;
    this._lastErrorTime = null;
    
    console.log('WebSocket disconnected');
    
    // Reset connection state immediately rather than delaying
    this.isConnecting = false;
    this.reconnectPending = false;
    this.socket = null;
    this._intentionalDisconnect = false;
    this.globalRequestCount = 0;
    
    console.log('WebSocket state fully reset');
  }
  
  /**
   * Force reconnection with authentication parameters
   * Used after login or when switching profiles to ensure authenticated connection
   * @param {Object} authParams - Authentication parameters from AuthContext
   * @returns {Promise<boolean>} - Resolves to true if reconnection was successful
   */
  reconnectWithAuth(authParams) {
    logger.info('WebSocket', 'Reconnecting with authentication parameters');
    
    // Force disconnect existing connection
    this.disconnect();
    
    // Wait for disconnect to complete before reconnecting
    return new Promise(resolve => {
      // Reduced delay to minimum necessary for socket to close properly
      setTimeout(async () => {
        try {
          // Connect with new auth parameters
          await this.connect(authParams);
          logger.info('WebSocket', 'Successfully reconnected with auth parameters');
          resolve(true);
        } catch (error) {
          logger.error('WebSocket', 'Failed to reconnect with auth parameters', error);
          resolve(false);
        }
      }, 50); // Minimum delay to allow socket cleanup
    });
  }

  /**
   * Update the authentication parameters for the WebSocket connection
   * This is called when the user logs in or switches profiles
   * @param {Object} authParams - Authentication parameters from AuthContext
   * @returns {Promise<boolean>} - Resolves to true if reconnection was successful
   */
  updateAuthParams(authParams) {
    // Check if auth params have changed
    const oldParamsStr = JSON.stringify(this._authParams || {});
    const newParamsStr = JSON.stringify(authParams || {});
    const hasChanged = oldParamsStr !== newParamsStr;
    
    // Store the new auth params
    this._authParams = authParams;
    
    // Log the auth param update
    logger.info('WebSocket', `Auth params updated: ${hasChanged ? 'changed' : 'unchanged'}`, {
      userId: authParams?.userId,
      profileId: authParams?.profileId
    });
    
    // If we're connected and auth changed, reconnect with new auth
    if (hasChanged && this.socket && this.socket.readyState === WebSocket.OPEN) {
      logger.info('WebSocket', 'Auth params changed, triggering reconnection');
      return this.reconnectWithAuth(authParams);
    }
    
    return Promise.resolve(true);
  }
  
  /**
   * Reset session state without disconnecting
   * Used when switching between users without a full page reload
   */
  resetSession() {
    logger.debug('WebSocket', 'Resetting session state');
    
    // Leave all active rooms
    for (const roomId of this._activeRooms) {
      logger.info('WebSocket', `Leaving room ${roomId} due to session reset`);
      try {
        // Send a leave message to the server
        this.sendMessage({
          type: 'leave_story',
          story_id: roomId
        }).catch(error => {
          // Ignore errors, we're resetting anyway
          logger.warn('WebSocket', `Error leaving room during reset: ${error.message}`);
        });
      } catch (error) {
        // Ignore any errors, we're resetting anyway
        logger.warn('WebSocket', `Error sending leave message: ${error.message}`);
      }
    }
    
    // Clear active rooms and current story ID
    this._activeRooms.clear();
    this.currentStoryId = null;
    
    // Create new handler collections instead of clearing the existing ones
    // This ensures any old subscriptions won't receive new messages
    this.handlers = {
      message: new Set(),
      error: new Set(),
      open: new Set(),
      close: new Set()
    };
    
    // Reset state tracking
    this.stateRequestCount = 0;
    this.stateRequestInProgress = false;
    this._errorCount = 0;
    this._lastErrorTime = null;
    
    logger.info('WebSocket', 'Session state has been reset');
  }
  
  /**
   * Check if the WebSocket is connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Send message to WebSocket server
   * @param {Object} message - Message to send
   * @returns {Promise} Promise that resolves when message is sent or rejects on error
   */
  sendMessage(message) {
    return new Promise((resolve, reject) => {
      // Enhanced debugging info for all messages
      if (process.env.NODE_ENV === 'development') {
        // Log the message type and WebSocket connection state
        console.log(`[WebSocket][DEBUG] Sending message type "${message.type}" with socket state:`, {
          readyState: this.socket ? 
            ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.socket.readyState] : 
            'NO_SOCKET',
          hasUrl: !!this.socket?.url,
          hasAuthentication: this.socket?.url?.includes('user_id=') || this.socket?.url?.includes('profile_id='),
          storyId: message.story_id || 'none'
        });
      }
      
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        this.connect()
          .then(() => {
            this._sendMessageInternal(message, resolve, reject);
          })
          .catch(error => {
            reject(new Error(`Failed to connect to WebSocket: ${error.message}`));
          });
      } else {
        this._sendMessageInternal(message, resolve, reject);
      }
    });
  }

  /**
   * Internal helper to send message
   */
  _sendMessageInternal(message, resolve, reject) {
    try {
      // Add logging for specific message types
      if (message.type === 'join_story') {
        console.log(`[WebSocket][AUTH-DEBUG] Sending join_story message for story ${message.story_id}:`, {
          messageType: message.type,
          storyId: message.story_id,
          socketUrl: this.socket?.url,
          hasAuthInUrl: this.socket?.url?.includes('user_id=') || false
        });
      }
      
      this.socket.send(JSON.stringify(message));
      resolve();
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      reject(error);
    }
  }

  /**
   * Schedule reconnection attempt
   */
  _scheduleReconnect() {
    // If we're intentionally disconnecting, don't schedule reconnect
    if (this._intentionalDisconnect) {
      this._logWarning('Not scheduling reconnect during intentional disconnect');
      return;
    }
  
    // If we already have a reconnect pending, don't schedule another
    if (this.reconnectPending) {
      this._logWarning('Reconnect already pending, not scheduling another');
      return;
    }
    
    // If we're already connecting, don't schedule reconnect
    if (this.isConnecting || this._connectionInProgress) {
      this._logWarning('Connection already in progress, not scheduling reconnect');
      return;
    }
    
    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Don't schedule reconnect if we're at global request limit
    if (this._isAtRequestLimit()) {
      this._logWarning('At global request limit, not scheduling reconnect');
      
      // Try REST API instead if possible
      if (this.currentStoryId && this.stateRequestCount < WS_STATE_MAX_REQUESTS && 
          !this.stateRequestInProgress && !this._intentionalDisconnect) {
        console.log('Falling back to REST API instead of reconnecting');
        setTimeout(() => {
          if (!this._intentionalDisconnect && !this.stateRequestInProgress) {
            this._fetchStateWithThrottle(this.currentStoryId).catch(() => {
              // Error already handled in _fetchStateWithThrottle
            });
          }
        }, 2000); // Delay a bit to allow state to settle
      }
      return;
    }
    
    if (this.reconnectAttempts < WS_MAX_RECONNECT_ATTEMPTS) {
      this.reconnectPending = true;
      this.reconnectAttempts++;
      console.log(`Scheduling reconnect attempt ${this.reconnectAttempts}/${WS_MAX_RECONNECT_ATTEMPTS}...`);
      
      // Exponential backoff for reconnect attempts (3s, 6s, 12s, etc.)
      const backoffInterval = WS_RECONNECT_INTERVAL * Math.pow(2, this.reconnectAttempts - 1);
      
      // Set a timeout to clear reconnectPending if we somehow get stuck
      const clearStuckTimeout = setTimeout(() => {
        if (this.reconnectPending) {
          this._logWarning('Reconnect pending flag was stuck, clearing it');
          this.reconnectPending = false;
        }
      }, backoffInterval + 15000); // Wait longer than the backoff interval
      
      this.reconnectTimer = setTimeout(() => {
        console.log(`Attempting to reconnect (${this.reconnectAttempts}/${WS_MAX_RECONNECT_ATTEMPTS})...`);
        
        if (this._intentionalDisconnect) {
          this._logWarning('Reconnect timer fired during intentional disconnect, aborting');
          this.reconnectPending = false;
          clearTimeout(clearStuckTimeout);
          return;
        }
        
        this.reconnectPending = false;
        
        this.connect().catch(error => {
          this._logError('Reconnection attempt failed:', error);
          
          clearTimeout(clearStuckTimeout);
          
          // If we have a current story ID, try to fall back to REST API
          if (this.currentStoryId && this.stateRequestCount < WS_STATE_MAX_REQUESTS && 
              !this.stateRequestInProgress && !this._isAtRequestLimit() && !this._intentionalDisconnect) {
            console.log('WebSocket reconnection failed, falling back to REST API');
            setTimeout(() => {
              if (!this._intentionalDisconnect && !this.stateRequestInProgress) {
                this._fetchStateWithThrottle(this.currentStoryId).catch(() => {
                  // Error already handled in _fetchStateWithThrottle
                });
              }
            }, 1000); // Small delay to let things settle
          }
        });
      }, backoffInterval);
    } else {
      this._logError('Max reconnection attempts reached');
      
      // Only try REST API if we're not already at the limit and no request is in progress
      if (this.currentStoryId && this.stateRequestCount < WS_STATE_MAX_REQUESTS && 
          !this.stateRequestInProgress && !this._isAtRequestLimit() && !this._intentionalDisconnect) {
        console.log('Max WebSocket reconnection attempts reached, falling back to REST API');
        setTimeout(() => {
          if (!this._intentionalDisconnect && !this.stateRequestInProgress) {
            this._fetchStateWithThrottle(this.currentStoryId).catch(() => {
              // Error already handled in _fetchStateWithThrottle
            });
          }
        }, 1000); // Small delay to let things settle
      } else {
        this._logWarning('Unable to reconnect or use REST API fallback, notifying of failure');
        // No more retries, notify error handlers
        this.handlers.error.forEach(handler => 
          handler(new Error('Failed to reconnect after multiple attempts'))
        );
      }
    }
  }

  /**
   * Subscribe to WebSocket events
   * @param {string} eventType - Type of event to subscribe to
   * @param {Function} handler - Event handler function
   */
  subscribe(eventType, handler) {
    if (!this.handlers[eventType]) {
      throw new Error(`Unknown event type: ${eventType}`);
    }
    
    // Check if the handler is already registered to avoid duplicates
    // This requires using the exact same function reference (not just similar code)
    if (this.handlers[eventType].has(handler)) {
      console.warn(`Handler already registered for ${eventType}, not adding duplicate`);
      return () => this.unsubscribe(eventType, handler);
    }
    
    // Add the handler
    this.handlers[eventType].add(handler);
    
    // Return unsubscribe function
    return () => this.unsubscribe(eventType, handler);
  }

  /**
   * Unsubscribe from WebSocket events
   * @param {string} eventType - Type of event to unsubscribe from
   * @param {Function} handler - Event handler function
   */
  unsubscribe(eventType, handler) {
    if (!this.handlers[eventType]) {
      throw new Error(`Unknown event type: ${eventType}`);
    }
    
    // Check if the handler exists before trying to delete
    if (!this.handlers[eventType].has(handler)) {
      console.warn(`Handler not found for ${eventType}, nothing to unsubscribe`);
      return;
    }
    
    // Remove the handler
    this.handlers[eventType].delete(handler);
  }

  /**
   * Join a room (story, chat, etc.) with proper tracking and authentication
   * @param {string} roomId - ID of the room to join
   * @param {Object} options - Options for joining the room
   * @param {string} options.messageType - Type of message to send (default: 'join_story')
   * @param {string} options.idField - Field name for the ID (default: 'story_id')
   * @param {boolean} options.force - Force rejoin even if already joined (default: false)
   * @returns {Promise} Promise that resolves when room is joined
   */
  joinRoom(roomId, options = {}) {
    // Don't join if we're intentionally disconnecting
    if (this._intentionalDisconnect) {
      this._logWarning('Not joining room during intentional disconnect');
      return Promise.reject(new Error('Disconnecting in progress'));
    }
    
    // Default options
    const messageType = options.messageType || 'join_story';
    const idField = options.idField || 'story_id';
    const force = options.force || false;
    
    // If we're already in this room, don't rejoin unless forced
    if (this._activeRooms.has(roomId) && !force) {
      // Only log in development to avoid console spam
      if (process.env.NODE_ENV === 'development') {
        logger.debug('WebSocket', `Already joined room ${roomId}, skipping duplicate join`);
      }
      return Promise.resolve();
    }
    
    // Add to active rooms set
    this._activeRooms.add(roomId);
    
    // If this is a story, set currentStoryId for backward compatibility
    if (messageType === 'join_story') {
      this.currentStoryId = roomId;
      
      // Reset state request counters
      this.stateRequestCount = 0;
      if (this.stateRequestTimer) {
        clearTimeout(this.stateRequestTimer);
        this.stateRequestTimer = null;
      }
      this.stateRequestInProgress = false;
    }
    
    // Check if we have auth params that aren't in the current connection
    const needsAuthReconnect = this._authParams && 
      (this._authParams.userId || this._authParams.profileId) && 
      this.socket && this.socket.readyState === WebSocket.OPEN && 
      this.socket.url && 
      // Need to check for both user_id and profile_id in the URL
      (!this.socket.url.includes('user_id=') || 
       (this._authParams.profileId && !this.socket.url.includes('profile_id=')));
    
    // Enhanced debug logging for auth parameters
    logger.info('WebSocket', `Join room ${roomId} auth check:`, {
      hasAuthParams: !!this._authParams,
      userId: this._authParams?.userId,
      profileId: this._authParams?.profileId,
      socketConnected: this.socket && this.socket.readyState === WebSocket.OPEN,
      socketUrl: this.socket?.url,
      needsAuthReconnect: needsAuthReconnect
    });
    
    // If we need to reconnect with auth, do that first
    if (needsAuthReconnect) {
      logger.info('WebSocket', 'Reconnecting with auth parameters before joining room');
      return this.reconnectWithAuth(this._authParams)
        .then(() => {
          logger.debug('WebSocket', `Auth reconnected, now joining room ${roomId}`);
          
          // Create the join message with the appropriate room ID field
          const message = {
            type: messageType
          };
          message[idField] = roomId;
          
          return this.sendMessage(message);
        })
        .catch(error => {
          this._logWarning('Auth reconnect failed, falling back to REST API:', error);
          
          if (messageType === 'join_story') {
            return this._fetchStateWithThrottle(roomId);
          } else {
            return Promise.reject(error);
          }
        });
    }
    
    // Check connection state
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      // If we're connected, just join
      logger.ws(`Joining room ${roomId} via WebSocket`);
      
      // Create the join message with the appropriate room ID field
      const message = {
        type: messageType
      };
      message[idField] = roomId;
      
      return this.sendMessage(message).catch(error => {
        this._logWarning('WebSocket join failed:', error);
        
        if (this._intentionalDisconnect) {
          return Promise.reject(new Error('Disconnecting in progress'));
        }
        
        if (messageType === 'join_story') {
          return this._fetchStateWithThrottle(roomId);
        } else {
          return Promise.reject(error);
        }
      });
    } else if (!this.isConnecting && !this._connectionInProgress && !this._intentionalDisconnect) {
      // Try to connect first with authentication parameters
      logger.debug('WebSocket', `Connecting WebSocket before joining room ${roomId}`);
      return this.connect()
        .then(() => {
          logger.debug('WebSocket', `Connected, now joining room ${roomId}`);
          
          // Create the join message with the appropriate room ID field
          const message = {
            type: messageType
          };
          message[idField] = roomId;
          
          return this.sendMessage(message);
        })
        .catch(error => {
          this._logWarning('Failed to connect before joining room:', error);
          
          if (this._intentionalDisconnect) {
            return Promise.reject(new Error('Disconnecting in progress'));
          }
          
          if (messageType === 'join_story') {
            return this._fetchStateWithThrottle(roomId);
          } else {
            return Promise.reject(error);
          }
        });
    } else {
      // We're already connecting or intentionally disconnecting
      this._logWarning(`Can't join room ${roomId} immediately`);
      
      if (messageType === 'join_story') {
        return this._fetchStateWithThrottle(roomId);
      } else {
        return Promise.reject(new Error('Connection in progress'));
      }
    }
  }
  
  /**
   * Register a component's usage of a room
   * @param {string} roomId - ID of the room
   * @param {string} componentId - Identifier for the component using the room
   */
  registerRoomUsage(roomId, componentId) {
    if (!roomId || !componentId) return;
    
    // Initialize the set if it doesn't exist
    if (!this._roomUsage[roomId]) {
      this._roomUsage[roomId] = new Set();
    }
    
    // Add this component to the set
    this._roomUsage[roomId].add(componentId);
    logger.debug('WebSocket', `Component ${componentId} registered usage of room ${roomId}`);
  }
  
  /**
   * Unregister a component's usage of a room
   * @param {string} roomId - ID of the room  
   * @param {string} componentId - Identifier for the component using the room
   */
  unregisterRoomUsage(roomId, componentId) {
    if (!roomId || !componentId || !this._roomUsage[roomId]) return;
    
    // Remove this component from the set
    this._roomUsage[roomId].delete(componentId);
    
    // If the set is now empty, delete it
    if (this._roomUsage[roomId].size === 0) {
      delete this._roomUsage[roomId];
    }
    
    logger.debug('WebSocket', `Component ${componentId} unregistered usage of room ${roomId}`);
  }
  
  /**
   * Check if a room is in use by any component
   * @param {string} roomId - ID of the room
   * @returns {boolean} True if the room is in use
   */
  isRoomInUse(roomId) {
    return !!this._roomUsage[roomId] && this._roomUsage[roomId].size > 0;
  }
  
  /**
   * Leave a room (story, chat, etc.)
   * @param {string} roomId - ID of the room to leave
   * @param {Object} options - Options for leaving the room
   * @param {string} options.messageType - Type of message to send (default: 'leave_story')
   * @param {string} options.idField - Field name for the ID (default: 'story_id')
   * @param {boolean} options.force - Force leave even if in use (default: false)
   * @returns {Promise} Promise that resolves when room is left
   */
  leaveRoom(roomId, options = {}) {
    // Default options
    const messageType = options.messageType || 'leave_story';
    const idField = options.idField || 'story_id';
    const force = options.force || false;
    
    // If we're not in this room, nothing to do
    if (!this._activeRooms.has(roomId)) {
      logger.debug('WebSocket', `Not in room ${roomId}, nothing to leave`);
      return Promise.resolve();
    }
    
    // Check if room is still in use by any components
    if (!force && this.isRoomInUse(roomId)) {
      logger.debug('WebSocket', `Not leaving room ${roomId} because it's still in use`);
      return Promise.resolve(false);
    }
    
    // Only try to send a leave message if the socket is open
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      logger.ws(`Leaving room ${roomId}`);
      
      // Create the leave message with the appropriate room ID field
      const message = {
        type: messageType
      };
      message[idField] = roomId;
      
      // Send the leave message and remove from active rooms
      return this.sendMessage(message)
        .then(() => {
          this._activeRooms.delete(roomId);
          
          // If this is a story, clear currentStoryId if it matches
          if (messageType === 'leave_story' && this.currentStoryId === roomId) {
            this.currentStoryId = null;
          }
          
          return true;
        })
        .catch(error => {
          // Still remove from active rooms even on error
          this._activeRooms.delete(roomId);
          
          // Clear currentStoryId if it matches
          if (messageType === 'leave_story' && this.currentStoryId === roomId) {
            this.currentStoryId = null;
          }
          
          logger.warn('WebSocket', `Error leaving room ${roomId}:`, error);
          return false;
        });
    } else {
      // Socket not open, just clean up local state
      this._activeRooms.delete(roomId);
      
      // Clear currentStoryId if it matches
      if (messageType === 'leave_story' && this.currentStoryId === roomId) {
        this.currentStoryId = null;
      }
      
      return Promise.resolve(false);
    }
  }
  
  /**
   * Simple helper to wait for connection to be ready
   * @private
   * @param {boolean} checkAuth - Whether to also check for authentication status
   * @returns {Promise<boolean>} Promise that resolves when connection is ready
   */
  _waitForConnection(checkAuth = false) {
    // If already connected and (not checking auth or already has auth in URL)
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      // If checking auth, make sure we have auth in the URL
      if (checkAuth) {
        const hasAuthInUrl = this.socket.url && 
                           (this.socket.url.includes('user_id=') || 
                            this.socket.url.includes('profile_id='));
        
        // If we need auth but don't have it in URL, we need to wait
        if (!hasAuthInUrl && this._authParams) {
          logger.debug('WebSocket', 'Connected but not authenticated, waiting for auth');
          // Don't return yet - fall through to waiting code
        } else {
          // Either we don't need auth or we already have it
          return Promise.resolve(true);
        }
      } else {
        // Not checking auth, just connection
        return Promise.resolve(true);
      }
    }
    
    // If intentionally disconnecting, reject immediately
    if (this._intentionalDisconnect) {
      return Promise.reject(new Error('Disconnecting in progress'));
    }
    
    // If no connection in progress, resolve immediately (will reconnect as needed)
    if (!this.isConnecting && !this._connectionInProgress) {
      return Promise.resolve(true);
    }
    
    // Otherwise, wait for connection to complete
    return new Promise((resolve) => {
      // Set a timeout to avoid waiting forever
      const timeout = setTimeout(() => {
        logger.warn('WebSocket', 'Connection wait timeout reached');
        resolve(false);
      }, 3000);
      
      // Create one-time event handler for connection completion
      const onOpen = () => {
        // If checking auth, make sure the URL has auth params
        if (checkAuth) {
          const hasAuthInUrl = this.socket && this.socket.url && 
                             (this.socket.url.includes('user_id=') || 
                              this.socket.url.includes('profile_id='));
          
          // If we need auth but don't have it in URL, keep waiting
          if (!hasAuthInUrl && this._authParams) {
            logger.debug('WebSocket', 'Connected but still waiting for auth params');
            return; // Don't resolve yet, continue waiting
          }
        }
        
        // Connection is ready (and auth is ready if we were checking it)
        clearTimeout(timeout);
        this.unsubscribe('open', onOpen);
        resolve(true);
      };
      
      // Subscribe to the open event
      this.subscribe('open', onOpen);
    });
  }

  /**
   * Join a story room with authentication parameters (backward compatibility method)
   * @param {string} storyId - ID of story to join
   * @param {Object} authParams - Optional authentication parameters from AuthContext
   * @param {boolean} force - Force rejoin even if already joined
   * @returns {Promise} Promise that resolves when story is joined
   */
  joinStory(storyId, authParams, force = false) {
    // If auth params are provided, update them
    if (authParams) {
      this.updateAuthParams(authParams);
    }
    
    // For authenticated stories, we need to wait for authentication too
    const needsAuth = !!this._authParams || !!authParams;
    
    // Wait for any in-progress connection to complete before trying to join
    // Also wait for authentication if needed
    return this._waitForConnection(needsAuth)
      .then((isReady) => {
        // If the connection is not ready (timed out waiting), fall back to REST API
        if (!isReady) {
          logger.info('WebSocket', `Connection not ready for story ${storyId}, using REST API`);
          return this._fetchStateWithThrottle(storyId);
        }
        
        // Use the more general joinRoom method
        return this.joinRoom(storyId, {
          messageType: 'join_story',
          idField: 'story_id',
          force: force
        });
      })
      .catch(error => {
        logger.error('WebSocket', `Failed to join story ${storyId} - connection not ready:`, error);
        
        // Fall back to REST API if connection isn't ready
        if (this._isAtRequestLimit() || this.stateRequestCount >= WS_STATE_MAX_REQUESTS) {
          return Promise.reject(error);
        }
        
        logger.info('WebSocket', `Falling back to REST API for story ${storyId}`);
        return this._fetchStateWithThrottle(storyId);
      });
  }
  
  /**
   * Fetch state from the REST API with throttling
   * @param {string} storyId - ID of story to fetch
   * @returns {Promise} Promise that resolves when state is fetched
   * @private
   */
  _fetchStateWithThrottle(storyId) {
    // Enhanced debugging log for REST API fallback
    console.log(`[WebSocket][AUTH-DEBUG] Falling back to REST API for story ${storyId}:`, {
      reason: 'WebSocket connection failed or not authenticated',
      storyId: storyId,
      socketStatus: this.socket ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.socket.readyState] : 'NO_SOCKET',
      socketUrl: this.socket?.url,
      hasAuthInUrl: this.socket?.url?.includes('user_id=') || false,
      requestCount: this.stateRequestCount,
      maxRequests: WS_STATE_MAX_REQUESTS
    });
    
    // Detailed connection state logging
    logger.debug('WebSocket', `Connection state check for REST API fallback:`, {
      intentionalDisconnect: this._intentionalDisconnect,
      isConnecting: this.isConnecting,
      connectionInProgress: this._connectionInProgress,
      reconnectPending: this.reconnectPending,
      socketState: this.socket ? ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.socket.readyState] : 'NO_SOCKET'
    });
    
    // IMPORTANT: Always allow REST API fallback during page refresh/load regardless of flag state
    const isPageRefreshOrLoad = document.readyState !== 'complete' || 
                               (window.performance && window.performance.navigation.type === 1);
                               
    // Don't fetch state if we're permanently disconnecting (not during reconnection or refresh)
    const isPermanentDisconnect = this._intentionalDisconnect && 
                                !this.isConnecting && 
                                !this._connectionInProgress &&
                                !this.reconnectPending &&
                                !isPageRefreshOrLoad;
                                
    if (isPermanentDisconnect) {
      this._logWarning('Not fetching state during permanent disconnect');
      return Promise.reject(new Error('Disconnecting in progress'));
    }
    
    // If we're reconnecting (temporary disconnect) or during page refresh, we should allow REST API access
    if (this._intentionalDisconnect) {
      this._logWarning('Allowing REST API access during WebSocket reconnection or page refresh');
    }
    
    // Don't retry if we've reached the maximum attempts
    if (this.stateRequestCount >= WS_STATE_MAX_REQUESTS) {
      this._logError(`Maximum state requests (${WS_STATE_MAX_REQUESTS}) reached for story ${storyId}`);
      return Promise.reject(new Error('Failed to connect after multiple attempts'));
    }
    
    // Don't make requests if we're at the global limit
    if (this._isAtRequestLimit()) {
      this._logWarning('At global request limit, not fetching state');
      return Promise.reject(new Error('Too many concurrent requests'));
    }
    
    // Don't make multiple requests at the same time
    if (this.stateRequestInProgress) {
      this._logWarning('State request already in progress, waiting...');
      return Promise.resolve(); // Just return immediately, we're already fetching
    }
    
    // Increment the request count and set the in-progress flag
    this.stateRequestCount++;
    this.stateRequestInProgress = true;
    this._incrementRequestCount();
    
    // Keep track of the current request ID to prevent race conditions
    const requestId = Date.now();
    this._currentRequestId = requestId;
    
    console.log(`Fetching story state via REST API (attempt ${this.stateRequestCount}/${WS_STATE_MAX_REQUESTS})`);
    
    // Force state request completion after timeout no matter what
    const masterTimeout = setTimeout(() => {
      if (this.stateRequestInProgress && this._currentRequestId === requestId) {
        this._logWarning('Master state request timeout reached, cleaning up');
        this.stateRequestInProgress = false;
        this._decrementRequestCount();
      }
    }, 20000); // 20 second master timeout
    
    // Import services directly to avoid potential issues with the statically imported version
    // This ensures we have a fresh reference even if the module state has changed
    const getServices = () => {
      try {
        // Dynamic import would be ideal, but for now use the global services object if available
        return window.services || require('./index').default;
      } catch (e) {
        // Fall back to the static import
        return { api: apiEndpoints };
      }
    };
    
    return Promise.resolve().then(() => {
        // Special case for page refresh: allow requests even during reconnection
        const isPageRefreshOrLoad = document.readyState !== 'complete' || 
                                   (window.performance && window.performance.navigation.type === 1);
        
        // Only cancel if it's a permanent disconnect (not during reconnection/refresh)
        if (this._intentionalDisconnect && !isPageRefreshOrLoad && 
            !this.isConnecting && !this._connectionInProgress) {
          clearTimeout(masterTimeout);
          this.stateRequestInProgress = false;
          this._decrementRequestCount();
          return Promise.reject(new Error('Request cancelled - permanent disconnect'));
        }
        
        // Still cancel if request ID doesn't match (replaced by newer request)
        if (this._currentRequestId !== requestId) {
          clearTimeout(masterTimeout);
          this.stateRequestInProgress = false;
          this._decrementRequestCount();
          return Promise.reject(new Error('Request replaced by newer request'));
        }
        
        // Set a timeout to abandon the request if it takes too long
        const requestTimeout = setTimeout(() => {
          if (this._currentRequestId === requestId) {
            this._logWarning('State request timeout, marking as complete');
            this.stateRequestInProgress = false;
            this._decrementRequestCount();
          }
        }, 10000); // 10 second timeout
        
        // Get a fresh reference to the services object
        const services = getServices();
        
        // Use the API client from services
        return services.api.story.getState(storyId)
          .then(response => {
            // Only process if this is still the current request
            if (this._currentRequestId !== requestId) {
              this._logWarning('Received response for outdated state request, ignoring');
              clearTimeout(requestTimeout);
              clearTimeout(masterTimeout);
              return Promise.resolve();
            }
            
            if (this._intentionalDisconnect) {
              this._logWarning('Received response during intentional disconnect, ignoring');
              clearTimeout(requestTimeout);
              clearTimeout(masterTimeout);
              this.stateRequestInProgress = false;
              this._decrementRequestCount();
              return Promise.resolve();
            }
            
            clearTimeout(requestTimeout);
            clearTimeout(masterTimeout);
            
            // Successfully got the state, reset the in-progress flag
            this.stateRequestInProgress = false;
            this._decrementRequestCount();
            
            // Notify any message handlers as if this came from the WebSocket
            this.handlers.message.forEach(handler => handler({
              type: 'story_update',
              story: response.data,
              is_initial_state: true
            }));
            
            // Try to reconnect websocket but with a small delay to avoid immediate reconnect
            if (!this._intentionalDisconnect) {
              setTimeout(() => {
                // Only try to reconnect if we're not in the middle of another request
                if (!this._intentionalDisconnect && !this.isConnecting && 
                    !this.reconnectPending && !this._isAtRequestLimit()) {
                  this.connect().catch(error => {
                    this._logWarning('Failed to reconnect WebSocket after state fetch:', error);
                  });
                }
              }, 5000); // Wait 5 seconds before trying to reconnect
            }
            
            return response;
          })
          .catch(error => {
            // Only process if this is still the current request
            if (this._currentRequestId !== requestId) {
              this._logWarning('Received error for outdated state request, ignoring');
              clearTimeout(requestTimeout);
              clearTimeout(masterTimeout);
              return Promise.resolve();
            }
            
            clearTimeout(requestTimeout);
            clearTimeout(masterTimeout);
            this.stateRequestInProgress = false;
            this._decrementRequestCount();
            
            if (this._intentionalDisconnect) {
              this._logWarning('Received error during intentional disconnect, ignoring');
              return Promise.resolve();
            }
            
            // Schedule another attempt after the throttle period, but only if we haven't reached the limit
            if (this.stateRequestCount < WS_STATE_MAX_REQUESTS) {
              this._logWarning(`State fetch failed, will retry in ${WS_STATE_REQUEST_THROTTLE/1000}s:`, error);
              
              // Clear any existing timer
              if (this.stateRequestTimer) {
                clearTimeout(this.stateRequestTimer);
                this.stateRequestTimer = null;
              }
              
              // Set up the next retry
              this.stateRequestTimer = setTimeout(() => {
                if (!this._intentionalDisconnect && !this._isAtRequestLimit()) {
                  this._fetchStateWithThrottle(storyId).catch(() => {
                    // Error is already handled in next call
                  });
                } else {
                  this._logWarning('Cannot retry state fetch (disconnect in progress or at request limit)');
                }
              }, WS_STATE_REQUEST_THROTTLE);
              
              return Promise.resolve(); // Don't propagate the error
            } else {
              // No more retries, propagate the error
              this._logError('All state fetch attempts failed:', error);
              
              // Notify error handlers
              this.handlers.error.forEach(handler => 
                handler(new Error('Failed to fetch story state after multiple attempts'))
              );
              
              return Promise.reject(error);
            }
          });
      })
      .catch(error => {
        this._logError('Error importing API endpoints:', error);
        clearTimeout(masterTimeout);
        this.stateRequestInProgress = false;
        this._decrementRequestCount();
        return Promise.reject(error);
      });
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;